/**
 * The Sticky-Centipede.
 *
 * Lore: a marine entity first logged on Level 8 as "a long, and scaled creature
 * with too many legs", named by the M.E.G. for the adhesive it anchors itself
 * to submerged pipes with. The archives fix the body plan — long, scaled, far
 * too many legs, at home in water — and say nothing about its colour, so the
 * yellow shell and the red dorsal stripe are ours.
 *
 * The whole design rides on one thing: it must not slide. A body that keeps a
 * rigid shape and follows its head is a caterpillar or a train. So the body is
 * not a shape at all — each plate is placed on the head's own recorded path,
 * one behind the next, and a travelling wave runs from the head to the tail
 * across the top of that. The legs run the same wave at the same rate, half a
 * cycle apart left to right. Nothing here is keyframed; the shape you see is
 * the last three metres of where it has actually been.
 */

import * as THREE from 'three';
import { clamp01, damp, lerp, smoothstep, TAU } from '../core/math';
import type { EntityState } from '../core/types';
import type { EntityView } from './api';
import { bakeRGB, freeAll, Kit, mergeParts, planarSpeed, thinBone, type Disposable } from './entkit';

/* ------------------------------------------------------------------ *
 * Proportions
 * ------------------------------------------------------------------ */

/** Plates behind the head. One pair of legs each, so twenty legs in all. */
const SEGMENTS = 10;
/** Metres of body per plate, measured along the path rather than along Z. */
const SEG_LEN = 0.235;
const HEAD_LEN = 0.26;
/** Half the width of the widest plate. Roughly a tenth of the total length. */
const HALF_W = 0.135;
/** Dorsal ridge height above the plate's centre line. */
const HALF_H = 0.11;
/**
 * Centre line above the floor. Everything is built around this, and it is set
 * from the legs: high enough that the tarsi still reach the carpet when the
 * body rolls into a crest, low enough that it is unmistakably a thing on the
 * floor rather than a thing standing on legs.
 */
const BODY_Y = 0.115;

/**
 * Per-plate width, front to back. Widest at the shoulders, pointed at the tail.
 *
 * Each entry must be at least the previous one times the plates' own rear
 * taper, or the plate in front is wider than the one behind it and the joint
 * becomes a hole you can see the floor through.
 */
const TAPER: readonly number[] = [0.95, 0.99, 1.0, 1.0, 0.97, 0.93, 0.88, 0.82, 0.75, 0.68];

/** Metres each plate is set forward into the one ahead, so no joint can open. */
const OVERLAP = 0.045;

/**
 * Cross-section of one plate, right half only, from the dorsal centre line
 * round to the belly. `x` is fractions of `HALF_W`, `y` of `HALF_H`.
 *
 * The two points at 0.24 and 0.33 exist only to carry the stripe: they are the
 * facet the red fades across, and keeping them close is what makes the edge of
 * the stripe read as an edge instead of a gradient. The belly is three points
 * because nobody will ever see it.
 */
const SECTION: ReadonlyArray<readonly [number, number]> = [
  [0.0, 1.0],
  [0.24, 0.96],
  [0.33, 0.92],
  [0.66, 0.76],
  [0.93, 0.42],
  [1.0, 0.0],
  [0.78, -0.34],
  [0.0, -0.5],
];

/** Fraction of the half-width the stripe covers. Sits between the two facets above. */
const STRIPE = 0.285;

/** The full closed ring: the right half, then the left half mirrored back up. */
function sectionRing(): Array<readonly [number, number]> {
  const ring: Array<readonly [number, number]> = SECTION.map((p) => p);
  for (let i = SECTION.length - 2; i >= 1; i--) ring.push([-SECTION[i]![0], SECTION[i]![1]]);
  return ring;
}

/* ------------------------------------------------------------------ *
 * Shell geometry
 * ------------------------------------------------------------------ */

const RING = sectionRing();
const RING_N = RING.length;

/**
 * One armoured plate: the cross-section swept from z=0 (front, toward the head)
 * to z=len (rear), scaled at each end. Caps are optional and duplicated rather
 * than shared, so the snout and the tail tip keep hard normals instead of
 * smearing into the sides.
 */
function makeShell(
  len: number,
  frontScale: number,
  rearScale: number,
  capFront: boolean,
  capRear: boolean
): THREE.BufferGeometry {
  const pos: number[] = [];
  const index: number[] = [];

  const push = (x: number, y: number, z: number): number => {
    pos.push(x, y, z);
    return pos.length / 3 - 1;
  };

  const ringAt = (z: number, scale: number): number[] =>
    RING.map(([x, y]) => push(x * HALF_W * scale, y * HALF_H * scale, z));

  const front = ringAt(0, frontScale);
  const rear = ringAt(len, rearScale);

  for (let k = 0; k < RING_N; k++) {
    const k2 = (k + 1) % RING_N;
    const a = front[k]!;
    const b = front[k2]!;
    const c = rear[k2]!;
    const d = rear[k]!;
    index.push(a, d, c, a, c, b);
  }

  // Centre of the cross-section, not of its bounding box: the belly reaches
  // further down than the ridge reaches up.
  const midY = HALF_H * 0.25;
  if (capFront) {
    const hub = push(0, midY * frontScale, 0);
    const dup = ringAt(0, frontScale);
    for (let k = 0; k < RING_N; k++) index.push(hub, dup[k]!, dup[(k + 1) % RING_N]!);
  }
  if (capRear) {
    const hub = push(0, midY * rearScale, len);
    const dup = ringAt(len, rearScale);
    for (let k = 0; k < RING_N; k++) index.push(hub, dup[(k + 1) % RING_N]!, dup[k]!);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return bakeShellColor(geo, len, frontScale, rearScale);
}

const SHELL = new THREE.Color();
const YELLOW = new THREE.Color().setHex(0xd9b02c);
const FLANK = new THREE.Color().setHex(0x8a6a15);
const RED = new THREE.Color().setHex(0xb0231b);

/**
 * Yellow shell, red down the spine, and a dark band where each plate slides
 * under the next. The stripe is baked per vertex rather than drawn as its own
 * strip so the whole animal is one buffer and one material: twenty-two meshes
 * of it already exist per instance without adding a second one for a marking.
 */
function bakeShellColor(
  geo: THREE.BufferGeometry,
  len: number,
  frontScale: number,
  rearScale: number
): THREE.BufferGeometry {
  return bakeRGB(geo, (x, y, z) => {
    const t = clamp01(z / len);
    const scale = lerp(frontScale, rearScale, t);
    const u = Math.abs(x) / (HALF_W * scale);
    const v = y / (HALF_H * scale);
    if (v > 0.2 && u < STRIPE) SHELL.copy(RED);
    else SHELL.copy(FLANK).lerp(YELLOW, smoothstep(-0.45, 0.85, v));
    // The seam is the last fifth of every plate, where the next one overlaps it.
    return SHELL.multiplyScalar(lerp(1, 0.74, smoothstep(0.7, 1.0, t)));
  });
}

/**
 * One leg: a femur out to the side and a tibia down from it, merged so a leg
 * costs one draw instead of two. Pre-bent rather than jointed — at fifteen
 * millimetres thick the knee angle is not what anyone is looking at, and the
 * twenty of them are.
 */
function makeLeg(side: number): THREE.BufferGeometry {
  const femur = thinBone(0.11, 0.019, 0.013, 3);
  femur.rotateZ(side * 1.31);
  const tibia = thinBone(0.105, 0.013, 0.004, 3);
  tibia.rotateZ(side * 0.8);
  tibia.translate(side * 0.106, -0.028, 0);
  return mergeParts([femur, tibia]);
}

interface CentipedeKit extends Disposable {
  head: THREE.BufferGeometry;
  plate: THREE.BufferGeometry;
  tail: THREE.BufferGeometry;
  legs: readonly [THREE.BufferGeometry, THREE.BufferGeometry];
  antenna: THREE.BufferGeometry;
  fang: THREE.BufferGeometry;
  shell: THREE.Material;
  limb: THREE.Material;
}

function buildCentipedeKit(): CentipedeKit {
  const head = makeShell(HEAD_LEN, 0.55, 0.86, true, false);
  const plate = makeShell(SEG_LEN * 1.16, 1.0, 0.88, false, false);
  const tail = makeShell(SEG_LEN * 1.4, 1.0, 0.1, false, true);
  const legs = [makeLeg(1), makeLeg(-1)] as const;
  const antenna = thinBone(0.16, 0.011, 0.003, 3);
  const fang = thinBone(0.1, 0.022, 0.005, 3);

  return {
    head,
    plate,
    tail,
    legs,
    antenna,
    fang,
    shell: new THREE.MeshLambertMaterial({ vertexColors: true }),
    // Legs and feelers are the same animal but thinner and paler, the way
    // chitin goes where it is not armour.
    limb: new THREE.MeshLambertMaterial({ color: 0xb58c22 }),
    dispose(): void {
      freeAll([head, plate, tail, legs[0], legs[1], antenna, fang, this.shell, this.limb]);
    },
  };
}

const centipedeKit = new Kit(buildCentipedeKit);

/* ------------------------------------------------------------------ *
 * The path it has taken
 * ------------------------------------------------------------------ */

/**
 * A ring buffer of where the head has been, keyed by distance travelled.
 *
 * This is the whole trick. Placing each plate at a fixed arc length back down
 * this path means the body traces the corridor the head actually walked — it
 * cannot cut a corner through a wall, and it bends round door frames for free,
 * neither of which a rigid chain of segments can do.
 */
class Trail {
  private readonly px: Float64Array;
  private readonly pz: Float64Array;
  private readonly cum: Float64Array;
  private head = -1;
  private n = 0;

  constructor(private readonly cap: number) {
    this.px = new Float64Array(cap);
    this.pz = new Float64Array(cap);
    this.cum = new Float64Array(cap);
  }

  /** Lay a straight body down behind a point, for the frame it appears on. */
  seed(x: number, z: number, dx: number, dz: number, span: number, step: number): void {
    this.head = -1;
    this.n = 0;
    for (let i = Math.ceil(span / step); i >= 0; i--) this.push(x - dx * i * step, z - dz * i * step);
  }

  push(x: number, z: number): void {
    const prev = this.head;
    const next = (prev + 1) % this.cap;
    this.cum[next] = prev < 0 ? 0 : this.cum[prev]! + Math.hypot(x - this.px[prev]!, z - this.pz[prev]!);
    this.px[next] = x;
    this.pz[next] = z;
    this.head = next;
    if (this.n < this.cap) this.n++;
  }

  /** Distance from the newest sample. Used to decide whether to record another. */
  driftFrom(x: number, z: number): number {
    if (this.n === 0) return Infinity;
    return Math.hypot(x - this.px[this.head]!, z - this.pz[this.head]!);
  }

  /**
   * Read a run of points at the given distances behind the head. `dists` must
   * ascend, which lets one backward walk answer all of them.
   */
  sampleChain(dists: readonly number[], outX: Float64Array, outZ: Float64Array): void {
    const oldest = (this.head - this.n + 1 + this.cap) % this.cap;
    const top = this.cum[this.head]!;
    let near = this.head;
    let far = (this.head - 1 + this.cap) % this.cap;
    for (let k = 0; k < dists.length; k++) {
      const target = top - dists[k]!;
      while (near !== oldest && this.cum[far]! > target) {
        near = far;
        far = (far - 1 + this.cap) % this.cap;
      }
      if (near === oldest) {
        outX[k] = this.px[oldest]!;
        outZ[k] = this.pz[oldest]!;
        continue;
      }
      const span = this.cum[near]! - this.cum[far]!;
      const t = span > 1e-6 ? clamp01((target - this.cum[far]!) / span) : 0;
      outX[k] = lerp(this.px[far]!, this.px[near]!, t);
      outZ[k] = lerp(this.pz[far]!, this.pz[near]!, t);
    }
  }
}

/** Metres of head travel between recorded samples. */
const TRAIL_STEP = 0.05;
/** Samples kept. `cap * TRAIL_STEP` has to comfortably exceed the body length. */
const TRAIL_CAP = 96;
/** A jump further than this in one frame is a respawn, not a sprint. */
const TELEPORT = 1.5;

/** Radians of phase between one plate and the next. About one and a half waves. */
const WAVE_LAG = 0.86;

/* ------------------------------------------------------------------ *
 * View
 * ------------------------------------------------------------------ */

/** Where each plate sits, measured back along the path from the tip of the head. */
const ARC: number[] = [];
for (let i = 0; i < SEGMENTS; i++) ARC.push(HEAD_LEN - OVERLAP + i * SEG_LEN);
/** Sampled half a plate further back, to take a heading from. */
const ARC_TAIL: number[] = ARC.map((a) => a + SEG_LEN * 0.55);

interface Plate {
  group: THREE.Group;
  hips: readonly [THREE.Group, THREE.Group];
}

/**
 * Build the Sticky-Centipede. Long, low, twenty legs, and it goes where it has
 * been rather than where it is pointing.
 */
export function createCentipedeView(): EntityView {
  const kit = centipedeKit.acquire();
  const object = new THREE.Group();

  const headGroup = new THREE.Group();
  const headMesh = new THREE.Mesh(kit.head, kit.shell);
  headMesh.position.y = BODY_Y;
  headMesh.castShadow = true;
  headGroup.add(headMesh);
  object.add(headGroup);

  // Forcipules: the modified front legs a centipede bites with, folded under
  // the head and pointing forward. They are the only part of it that opens.
  const fangs: THREE.Group[] = [];
  for (const side of [-1, 1] as const) {
    const fang = new THREE.Group();
    fang.position.set(side * 0.035, BODY_Y - 0.03, 0.02);
    fang.rotation.set(1.15, side * 0.35, 0);
    fang.add(new THREE.Mesh(kit.fang, kit.limb));
    headGroup.add(fang);
    fangs.push(fang);
  }

  const feelerRoots: THREE.Group[] = [];
  const feelerTips: THREE.Group[] = [];
  for (const side of [-1, 1] as const) {
    const root = new THREE.Group();
    root.position.set(side * 0.045, BODY_Y + 0.05, 0.03);
    root.add(new THREE.Mesh(kit.antenna, kit.limb));
    const tip = new THREE.Group();
    tip.position.y = -0.16;
    tip.add(new THREE.Mesh(kit.antenna, kit.limb));
    root.add(tip);
    headGroup.add(root);
    feelerRoots.push(root);
    feelerTips.push(tip);
  }

  const plates: Plate[] = [];
  for (let i = 0; i < SEGMENTS; i++) {
    const group = new THREE.Group();
    const scale = TAPER[i]!;
    const mesh = new THREE.Mesh(i === SEGMENTS - 1 ? kit.tail : kit.plate, kit.shell);
    mesh.scale.set(scale, scale, 1);
    mesh.position.y = BODY_Y;
    mesh.castShadow = true;
    group.add(mesh);

    const hips: THREE.Group[] = [];
    for (const side of [-1, 1] as const) {
      const hip = new THREE.Group();
      hip.position.set(side * HALF_W * scale * 0.82, BODY_Y, SEG_LEN * 0.45);
      hip.add(new THREE.Mesh(kit.legs[side > 0 ? 0 : 1]!, kit.limb));
      group.add(hip);
      hips.push(hip);
    }
    object.add(group);
    plates.push({ group, hips: [hips[0]!, hips[1]!] });
  }

  const trail = new Trail(TRAIL_CAP);
  const bx = new Float64Array(SEGMENTS);
  const bz = new Float64Array(SEGMENTS);
  const tx = new Float64Array(SEGMENTS);
  const tz = new Float64Array(SEGMENTS);

  // Per-instance so two of them never crest together. Cosmetic only.
  const phase = Math.random() * TAU;
  let wave = phase;
  let ready = false;
  let rear = 0;
  let dead = 0;

  return {
    object,
    update(state: EntityState, dt, _time, _camera) {
      const px = state.pos.x;
      const pz = state.pos.z;
      if (!ready || trail.driftFrom(px, pz) > TELEPORT) {
        trail.seed(px, pz, Math.sin(state.yaw), -Math.cos(state.yaw), SEG_LEN * (SEGMENTS + 2), TRAIL_STEP);
        ready = true;
      } else if (trail.driftFrom(px, pz) >= TRAIL_STEP) {
        trail.push(px, pz);
      }

      object.position.set(px, 0, pz);

      const speed = planarSpeed(state);
      const drive = clamp01(speed / 3.4);
      rear = damp(rear, state.mode === 'attack' ? 1 : 0, 7, dt);
      dead = damp(dead, state.mode === 'dead' ? 1 : 0, 1.6, dt);
      const alive = 1 - dead;

      // The wave runs at the rate the legs are stepping, so a stopped
      // centipede holds its shape and a running one crests about twice a
      // second. It never stops entirely: this thing breathes with its whole
      // length.
      wave += dt * (1.5 + speed * 5.4) * alive;

      trail.sampleChain(ARC, bx, bz);
      trail.sampleChain(ARC_TAIL, tx, tz);

      const amp = (0.018 + 0.06 * drive) * alive;
      for (let i = 0; i < SEGMENTS; i++) {
        // Heading first, from the raw path, so the sideways offset is applied
        // across the body rather than along it.
        let fx = bx[i]! - tx[i]!;
        let fz = bz[i]! - tz[i]!;
        const l = Math.hypot(fx, fz) || 1;
        fx /= l;
        fz /= l;

        const swing = Math.sin(wave - i * WAVE_LAG);
        // Death is a slow curl to one side, the way an arthropod goes.
        const curl = dead * 0.055 * i;
        const off = amp * swing + curl;
        const plate = plates[i]!;
        const lift = rear * Math.max(0, 1 - i * 0.34) * 0.34;
        plate.group.position.set(
          bx[i]! - px - fz * off,
          lift + Math.sin(wave - i * WAVE_LAG + 1.1) * 0.011 * drive - dead * 0.045,
          bz[i]! - pz + fx * off
        );
        plate.group.rotation.set(lift * 0.9, Math.atan2(-fx, -fz), -swing * 0.1 * drive);

        for (let s = 0; s < 2; s++) {
          const hip = plate.hips[s]!;
          const p = wave - i * WAVE_LAG + (s === 0 ? 0 : Math.PI);
          const side = s === 0 ? -1 : 1;
          // Fore-aft is the stroke; the roll off the hip is the recovery, and
          // a dead one pulls every leg in under itself.
          hip.rotation.y = Math.sin(p) * (0.22 + 0.5 * drive) * alive;
          hip.rotation.z =
            side * (Math.max(0, Math.sin(p + 0.9)) * (0.1 + 0.42 * drive) * alive + dead * 0.95);
        }
      }

      // The head is a plate's length ahead of the first segment, so it takes
      // its heading from the gap between them rather than borrowing one and
      // disagreeing with the body about which way the corridor went.
      const first = plates[0]!;
      let hx = -first.group.position.x;
      let hz = -first.group.position.z;
      const hl = Math.hypot(hx, hz) || 1;
      hx /= hl;
      hz /= hl;
      headGroup.position.y = first.group.position.y + rear * 0.16;
      headGroup.rotation.set(rear * 0.6, Math.atan2(-hx, -hz), first.group.rotation.z * 0.6);

      // Feelers never stop. Two lengths of tapered wire sweeping the air in
      // front of it, faster the moment it has something to sweep at.
      const cast = 0.5 + state.awareness * 0.5;
      for (let i = 0; i < 2; i++) {
        const s = i === 0 ? -1 : 1;
        const t = wave * 0.55 + i * 1.7;
        feelerRoots[i]!.rotation.set(1.95 + Math.sin(t) * 0.26 * cast, s * (0.45 + Math.sin(t * 0.7) * 0.3 * cast), 0);
        feelerTips[i]!.rotation.x = 0.3 + Math.sin(t * 1.3 + 0.8) * 0.22 * cast;
      }

      for (let i = 0; i < 2; i++) fangs[i]!.rotation.y = (i === 0 ? -1 : 1) * (0.35 + rear * 0.75);
    },
    dispose() {
      object.clear();
      centipedeKit.release();
    },
  };
}
